import React from "react";
import { format } from "date-fns";
import { AlertTriangle, ChevronDown, ChevronUp, Smile, UserPlus, Users } from "lucide-react";
import { ReviewOverviewSnapshotHighlight, ReviewOverviewSnapshotHighlightCategory } from "@/services/bedrock";
import { cn } from "@/lib/utils";

export const CATEGORY_ORDER: ReviewOverviewSnapshotHighlightCategory[] = [
  "strengths",
  "needs_attention",
  "referral_opportunities",
  "team",
];

export const CATEGORY_CONFIG: Record<
  ReviewOverviewSnapshotHighlightCategory,
  { label: string; icon: React.ElementType; className: string }
> = {
  strengths: {
    label: "What clients love",
    icon: Smile,
    className: "text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/30",
  },
  needs_attention: {
    label: "Needs attention",
    icon: AlertTriangle,
    className: "text-rose-700 bg-rose-50 dark:text-rose-400 dark:bg-rose-950/30",
  },
  referral_opportunities: {
    label: "Referral opportunities",
    icon: UserPlus,
    className: "text-sky-700 bg-sky-50 dark:text-sky-400 dark:bg-sky-950/30",
  },
  team: {
    label: "Team feedback",
    icon: Users,
    className: "text-indigo-700 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-950/30",
  },
};

export function PeriodInsights({
  summary,
  highlights,
  generatedAt,
  showTeamHighlights,
}: {
  summary?: string | null;
  highlights?: ReviewOverviewSnapshotHighlight[] | null;
  generatedAt?: string | null;
  showTeamHighlights: boolean;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const trimmedSummary = summary?.trim();

  React.useEffect(() => {
    setExpanded(false);
  }, [trimmedSummary, generatedAt]);
  const visibleHighlights = (highlights ?? []).filter(h => {
    if (!h.text?.trim()) return false;
    if (!CATEGORY_ORDER.includes(h.category)) return false;
    if (h.category === "team" && !showTeamHighlights) return false;
    return true;
  });

  if (!trimmedSummary && visibleHighlights.length === 0) return null;

  const byCategory = CATEGORY_ORDER.reduce((acc, category) => {
    const items = visibleHighlights.filter(h => h.category === category);
    if (items.length > 0) acc[category] = items;
    return acc;
  }, {} as Partial<Record<ReviewOverviewSnapshotHighlightCategory, ReviewOverviewSnapshotHighlight[]>>);

  const categories = CATEGORY_ORDER.filter(category => byCategory[category]);
  const canCollapse = categories.length > 0 || (trimmedSummary != null && trimmedSummary.length > 140);

  const toggle = () => {
    if (!canCollapse) return;
    setExpanded(prev => !prev);
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50 via-orange-50/40 to-sky-50/70 px-6 py-5 font-sans shadow-[inset_0_1px_0_0_rgba(255,255,255,0.7)] transition-[border-color,box-shadow] dark:border-amber-900/40 dark:from-amber-950/40 dark:via-orange-950/20 dark:to-sky-950/30 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]",
        canCollapse &&
          "cursor-pointer hover:border-amber-300/80 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.85),0_8px_24px_-16px_rgba(245,158,11,0.45)] dark:hover:border-amber-800/55"
      )}
      onClick={toggle}
      onKeyDown={
        canCollapse
          ? event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggle();
              }
            }
          : undefined
      }
      tabIndex={canCollapse ? 0 : undefined}
      role={canCollapse ? "button" : undefined}
      aria-expanded={canCollapse ? expanded : undefined}
      aria-label={canCollapse ? (expanded ? "Collapse insights" : "Expand insights") : undefined}
    >
      <div
        className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-amber-300/25 blur-2xl dark:bg-amber-500/10"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-sky-300/20 blur-2xl dark:bg-sky-500/10"
        aria-hidden
      />

      <div className="relative flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1 font-bold text-lg text-slate-950 font-sans">
          Insights from
          <img alt="SocialCoach AI" src="https://cdn.socialcoach.io/socoai_color_positive.svg" className="h-[15px]" />
        </div>
        <div className="flex items-center gap-3">
          {generatedAt && (
            <p className="text-xs text-muted-foreground">Updated {format(new Date(generatedAt), "MMM d, yyyy")}</p>
          )}
          {canCollapse &&
            (expanded ? (
              <ChevronUp className="h-4 w-4 text-amber-800/60 dark:text-amber-200/60" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4 text-amber-800/60 dark:text-amber-200/60" aria-hidden />
            ))}
        </div>
      </div>

      <div
        className={cn(
          "relative mt-3",
          canCollapse &&
            !expanded &&
            "max-h-[4.25rem] overflow-hidden [mask-image:linear-gradient(to_bottom,black_0%,black_35%,transparent_100%)]"
        )}
      >
        {trimmedSummary && <p className="max-w-3xl text-base leading-relaxed text-foreground/90">{trimmedSummary}</p>}

        {(expanded || !canCollapse) && categories.length > 0 && (
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {categories.map(category => {
              const config = CATEGORY_CONFIG[category];
              const Icon = config.icon;
              const items = byCategory[category] ?? [];
              return (
                <div
                  key={category}
                  className="rounded-xl border border-white/70 bg-white/55 px-4 py-3 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-background/40"
                >
                  <div className="mb-2 inline-flex items-center gap-1.5">
                    <span className={cn("rounded-full p-1", config.className)}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {config.label}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {items.map((item, index) => (
                      <li key={`${category}-${index}`} className="text-sm leading-relaxed text-foreground">
                        {item.text}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {canCollapse && (
        <p className="relative mt-3 text-xs font-medium text-amber-900/55 dark:text-amber-100/50">
          {expanded ? "Click to collapse" : "Click to read more"}
        </p>
      )}
    </div>
  );
}
